import React, { use } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ElevatedButton from 'shared/components/buttons/elevated-button/elevated-button';
import FullPageLoadingIndicator from 'shared/components/loading-indicator/full-page-loading-indicator';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import ViewingModeController from 'shared/components/viewing-mode-controller/viewing-mode-controller';

import { Tenant } from 'features/tenants/models/tenant';
import TenantsProviderContext from 'features/tenants/providers/tenants-provider/contexts/tenants-provider-context';
import styles from './tenant-details-information.module.scss';

const TenantDetailsInformation = (): React.JSX.Element => {
  const { t } = useTranslation('tenants');
  const {
    formState: { errors },
    control,
  } = useFormContext<Tenant>();

  const { viewingMode, currentTenant, isUpdatingTenant, onAccessTenant, accessTenantIsLoading, allowOnAccessTenant } =
    use(TenantsProviderContext);

  const renderAccessButton = (): React.JSX.Element | null =>
    viewingMode === 'viewing' && currentTenant && allowOnAccessTenant() ? (
      <ElevatedButton
        icon={<OpenInNewIcon />}
        label={t('tenantDetails.accessTenantButton')}
        onClick={() => onAccessTenant(currentTenant)}
      />
    ) : null;

  const renderNoteField = (): React.JSX.Element | null =>
    !(viewingMode === 'viewing' && !currentTenant?.note) ? (
      <ViewingModeController
        viewingMode={viewingMode}
        name="note"
        control={control as unknown as Control}
        label={t('tenantDetails.noteLabel')}
        render={({ field }) => (
          <TextField
            {...field}
            slotProps={{
              htmlInput: {
                'data-testid': 'noteInput',
                maxLength: 255,
              },
            }}
            id="noteInput"
            label={t('tenantDetails.noteLabel')}
            variant="outlined"
            error={!!errors.note}
            helperText={errors.note?.message?.toString()}
            multiline
            minRows={8}
            maxRows={12}
            className={styles.formField}
          />
        )}
      />
    ) : null;

  const renderContent = (): React.JSX.Element => (
    <>
      {renderAccessButton()}
      {renderNoteField()}
    </>
  );

  return (
    <div className={styles.tenantDetailsInformation}>
      <Typography variant="h2" className={styles.tenantTitle} data-testid="tenantPopupTitle">
        {currentTenant?.displayName}
      </Typography>
      <div className={styles.informationPartContainer} data-testid="tenantPopupNoteContainer">
        {isUpdatingTenant ? <LoadingIndicator /> : renderContent()}
      </div>
      <FullPageLoadingIndicator isLoading={accessTenantIsLoading} useTimeout={false} />
    </div>
  );
};

export default TenantDetailsInformation;
